import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { GisMapWrapper } from "@gis/GisMapWrapper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@store/store";
import MapPanel from "@modules/stepper-row/components/MapPanel";
import { ApplicantDetailsPreviewROWResponse } from "../application-preview/types";
import { ApplicantDetailsROWResponse } from "../application-details/types";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { useNavigate } from "react-router-dom";
import { setActiveStep } from "@modules/stepper-row/redux/stepperROWSlice";
import { setAmenityROWField } from "@modules/stepper-row/redux/amenitesROWSlice";
import { UtilityTableProps } from "../application-details-row/types";

export const AmenityLocationSectionROW: React.FC<{
  isExpanded: boolean;
  toggleExpand: () => void;
  visible?: boolean;
  data: ApplicantDetailsPreviewROWResponse | ApplicantDetailsROWResponse | any;
}> = ({ isExpanded, toggleExpand, data, visible = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (
      data &&
      start_latitude !== undefined &&
      start_longitude !== undefined &&
      end_latitude !== undefined &&
      end_longitude !== undefined
    ) {
      dispatch(
        setAmenityROWField({
          name: `start_latitude`,
          value: start_latitude ?? "",
        }),
      );
      dispatch(
        setAmenityROWField({
          name: `start_longitude`,
          value: start_longitude ?? "",
        }),
      );
      dispatch(
        setAmenityROWField({
          name: `end_latitude`,
          value: end_latitude ?? "",
        }),
      );
      dispatch(
        setAmenityROWField({
          name: `end_longitude`,
          value: end_longitude ?? "",
        }),
      );
    }
  }, [data]);

  if (!data) return null;

  const {
    applocation_category,
    national_highway_number,
    state,
    district,
    amenity,
    pincode,
    khasra_number,
    address_1,
    address_2,
    highway_category,
    latitude,
    longitude,
    highway_side,
    application_code,
    application_status,
    utility_type,
    other_utility_type,
    start_chainageKm,
    start_chainageM,
    end_chainageKm,
    end_chainageM,
    start_chainage,
    end_chainage,
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    ro_office_location,
    piu_office_location,
    circle_rate,
    utilized_nh_land_area,
    taluka,
    village,
    villages_crossed_names,
    villages_crossed_count,
    tehsils_crossed_names,
    tehsils_crossed_count,
    proposed_utility_alignment,
  } = data;

  const displayValue = (value: string | number | null) => value ?? "-";
  const hasValue = (value: any) =>
    value !== null && value !== undefined && value !== "";

  const getChainagePart = (
    value: any,
    chainage: string | undefined,
    index: number,
  ) => {
    return hasValue(value) ? value : (chainage?.split("+")[index] ?? "-");
  };
  const villageDisplay =
    Array.isArray(villages_crossed_names) && villages_crossed_names.length
      ? villages_crossed_names.join(", ")
      : "";

  const tehsilDisplay =
    Array.isArray(tehsils_crossed_names) && tehsils_crossed_names.length
      ? tehsils_crossed_names.join(", ")
      : "";

  const amenityDetails = [
    { label: "Type of Utility", value: displayValue(utility_type) },
    { label: "Utility", value: displayValue(amenity) },
    ...(other_utility_type
      ? [
          {
            label: "Other Utility Type",
            value: displayValue(other_utility_type),
          },
        ]
      : []),
    { label: "Highway Category", value: displayValue(highway_category) },
    { label: "Highway Number", value: displayValue(national_highway_number) },
    { label: "Side of Highway", value: displayValue(highway_side) },
    {
      label: "Proposed Utility Alignment",
      value: displayValue(proposed_utility_alignment),
    },
    {
      label: "Number of Villages Passes Through",
      value: displayValue(villages_crossed_count),
    },
    { label: "Villages Passes Through", value: displayValue(villageDisplay) },
    {
      label: "Number of Tehsils Passes Through",
      value: displayValue(tehsils_crossed_count),
    },
    { label: "Tehsil Passed Through", value: displayValue(tehsilDisplay) },
    { label: "Taluka", value: displayValue(taluka) },
    { label: "District", value: displayValue(district) },
    { label: "State", value: displayValue(state) },
    { label: "Pincode", value: displayValue(pincode) },
  ];

  const detailsLayout = [
    [amenityDetails[0], amenityDetails[1]],
    [amenityDetails[2], amenityDetails[3]],
    [amenityDetails[4], amenityDetails[5]],
    [amenityDetails[6], amenityDetails[7]],
    [amenityDetails[8], amenityDetails[9]],
    [amenityDetails[10], amenityDetails[11], amenityDetails[12]],
  ];

  const alongUtilities = data?.chainage_details || [];
  const acrossUtilities = data?.crossing_details || [];

  const alignment = proposed_utility_alignment?.toLowerCase();

  const showAlong =
    alignment?.includes("along") || alignment === "along the highway";

  const showAcross =
    alignment?.includes("across") || alignment === "across the highway";

  const UtilityTable = ({
    title,
    rows,
    columns,
  }: {
    title: string;
    rows: any[];
    columns: string[];
  }) => {
    if (!rows?.length) return null;

    return (
      <Box mt={3}>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "#111827",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            mt: 1,
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "100%",

              "& .MuiTableCell-root": {
                borderBottom: "1px solid #E5E7EB",
                py: "14px",
                px: "12px",
                fontSize: "13px",
                color: "#2F2F2F",
                textAlign: "left",
                verticalAlign: "middle",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },

              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#F7F8FA",
                color: "#6B7280",
                fontWeight: 500,
                fontSize: "12px",
                textAlign: "left",
              },

              "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
                {
                  borderBottom: "none",
                },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: `${100 / (columns.length + 1)}%`,
                    fontWeight: 600,
                  }}
                >
                  S. No
                </TableCell>

                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      width: `${100 / (columns.length + 1)}%`,
                      fontWeight: 600,
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((item, index) => (
                <TableRow
                  key={item.id || index}
                  sx={{
                    "&:hover": {
                      background: "transparent",
                    },
                  }}
                >
                  <TableCell align="left">{index + 1}</TableCell>

                  {columns.map((column) => {
                    let value = "-";

                    switch (column) {
                      case "Start Chainage (Km)":
                        value = `${item.start_chainage_km} + ${item.start_chainage_m}`;
                        break;

                      case "End Chainage (Km)":
                        value = `${item.end_chainage_km} + ${item.end_chainage_m}`;
                        break;

                      case "Crossing Chainage (Km)":
                        value = `${item.crossing_chainage_km} + ${item.crossing_chainage_m}`;
                        break;

                      case "Side":
                        value = item.side;
                        break;

                      case "Length (Km)":
                        value = item.length_km;
                        break;

                      case "No. of cables & dia of protection pipe":
                        value = item.cable_pipe_details;
                        break;
                    }

                    return (
                      <TableCell key={column} align="left">
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Card sx={{ boxShadow: "none", border: "1px solid #E7E8E9" }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#F4F2F7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E7E8E9",
            pl: 2,
            py: "5px",
          }}
        >
          <Typography
            sx={{ fontSize: 12 }}
            fontWeight="semi-bold"
            className="preview-dropdown-header"
          >
            Utility & Location Details
            {visible && (
              <IconButton
                className="ml-4"
                onClick={() => {
                  dispatch(setActiveStep(0));
                  navigate("/stepper-row");
                }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            )}
          </Typography>
          <IconButton aria-label="Collapse section" onClick={toggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        {/* Content */}
        {isExpanded && ( // Conditionally render the content
          <CardContent>
            <Grid container spacing={4}>
              {/* Map at the top */}
              {
                <Grid item xs={12}>
                  <MapPanel
                    hideRemovePin={true}
                    startChainage={`${start_chainageKm || 0}+${start_chainageM || 0}`}
                    endChainage={`${end_chainageKm || 0}+${end_chainageM || 0}`}
                    geoCoordinates={{
                      latitude: latitude || 0,
                      longitude: longitude || 0,
                    }}
                    start_coordinates={{
                      latitude: start_latitude || 0,
                      longitude: start_longitude || 0,
                    }}
                    end_coordinates={{
                      latitude: end_latitude || 0,
                      longitude: end_longitude || 0,
                    }}
                    // sideofHighway={highway_side}
                    // chainages={chainage}
                    onMapClick={() => {}}
                    isFirstSectionCompletedRow={true}
                  />
                </Grid>
              }

              {/* Amenity details in 4 columns */}
              {/* Type of Amenity to Pincode */}
              {detailsLayout?.flat()?.map((detail, index) => (
                <Grid key={index} item xs={12} sm={6} md={3}>
                  <Typography
                    className="preview-dropdown-label"
                    sx={{ fontWeight: 500, mb: 0.5, color: "#878787" }}
                  >
                    {detail.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "1rem" }}
                    className="preview-dropdown-value"
                  >
                    {detail.value ?? "-"}
                  </Typography>
                </Grid>
              ))}
              {!visible && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography className="preview-dropdown-label">
                    Circle Rate (per sq.)
                  </Typography>
                  <Typography className="preview-dropdown-value">
                    {displayValue(circle_rate)}
                  </Typography>
                </Grid>
              )}
              {/* Start Chainage */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                  Start Chainage
                </Typography>
                <Box className="flex gap-20">
                  <Box>
                    <Typography className="preview-dropdown-label">
                      Km
                    </Typography>
                    <Typography className="preview-dropdown-value">
                      {getChainagePart(start_chainageKm, start_chainage, 0)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography className="preview-dropdown-label">
                      m
                    </Typography>
                    <Typography className="preview-dropdown-value">
                      {getChainagePart(start_chainageM, start_chainage, 1)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {/* End Chainage */}
              {Boolean(end_chainageKm || end_chainage?.split("+")[0]) && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                    End Chainage
                  </Typography>
                  <Box className="flex gap-20">
                    <Box>
                      <Typography className="preview-dropdown-label">
                        Km
                      </Typography>
                      <Typography className="preview-dropdown-value">
                        {getChainagePart(end_chainageKm, end_chainage, 0)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography className="preview-dropdown-label">
                        m
                      </Typography>
                      <Typography className="preview-dropdown-value">
                        {getChainagePart(end_chainageM, end_chainage, 1)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {ro_office_location && (
                <Grid item xs={6} sm={3}>
                  <Typography className="preview-dropdown-label">
                    RO Office
                  </Typography>
                  <Typography className="preview-dropdown-value">
                    {displayValue(ro_office_location)}
                  </Typography>
                </Grid>
              )}
              {piu_office_location && (
                <Grid item xs={6} sm={3}>
                  <Typography className="preview-dropdown-label">
                    Project Implementation Unit Office
                  </Typography>
                  <Typography className="preview-dropdown-value">
                    {piu_office_location
                      ?.map((data: any) => data?.office_name)
                      .join(", ") || "-"}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <UtilityTable
              title="Utility Details (Along the Highway)"
              rows={data?.chainage_Details || []}
              columns={[
                "Start Chainage (Km)",
                "End Chainage (Km)",
                "Side",
                "Length (Km)",
                "No. of cables & dia of protection pipe",
              ]}
            />
            <UtilityTable
              title="Utility Details (Across the Highway)"
              rows={data?.crossing_Details || []}
              columns={[
                "Crossing Chainage (Km)",
                "Length (Km)",
                "No. of cables & dia of protection pipe",
              ]}
            />
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
